import Section from "../common/section"
import { getThemeClass, getSectionPaddingClass } from "~/styles/styles"
import Image, { type StaticImageData } from "next/image"
import { type IconType } from "../icon"

export type SplitTextProps = {
  theme?: string
  bgColour?: string
  title?: string | JSX.Element
  pretitle?: string | JSX.Element
  sectionTitle?: string | JSX.Element
  inlineTitle?: string
  titleColour?: string

  body?: string | string[] | JSX.Element
  bodyWidth?: string
  bodyColour?: string
  afterAction?: JSX.Element

  image?: string | string[] | StaticImageData
  imgSize?: string
  imgTop?: boolean // text bottom, image top -- on certain cases, mobile and desktop alignment are different
  imageClass?: string
  extraImageClass?: string
  iconImageClass?: string

  textRight?: boolean // text right, div(image) left
  customDiv?: JSX.Element

  padding?: string
  textPadding?: string
  customPadding?: boolean // padding for mobile testimonial section
  maxWidth?: string
  customTextWidth?: string
  centredOnMobile?: boolean // centred items on small screens

  itemsClass?: string // custom grid/flex class
  alignItemsClass?: string // items alignment - i.e.items-center
  gap?: string
  flexHeight?: boolean // section flex height -- no set min height

  // heart icon image
  iconImage?: string | StaticImageData
  iconImgLeft?: boolean
  iconImgPosition?: string
  hideIconImageOnMobile?: boolean

  customBorder?: string
}

const SplitTextSection = (props: SplitTextProps) => {
  const body = Array.isArray(props.body)
    ? props.body.map((line, i) => (
        <p key={i} className="">
          {line}
        </p>
      ))
    : props.body

  const images =
    Array.isArray(props.image) && props.image.length > 1 ? (
      props.image.map((img: string, i: number) => <Image key={i} src={img} alt="image" width={1000} height={1000} className={`object-cover md:mx-auto md:h-full ${props.imgSize ?? ""}`} />)
    ) : (
      <Image src={props.image as string} alt="image" width={1000} height={1000} className={`object-cover md:mx-auto md:h-full ${props.imgSize ?? ""}`} />
    )

  const titleClass = `font-bold ${props.theme === "light" ? "text-purpleblue" : props.titleColour ? props.titleColour : "text-white"}`
  const padding = `${props.padding ?? `${getSectionPaddingClass("default")} 2xl:px-0`} ${props.customPadding ? "pb-48 xs:pb-48 sm:pb-48" : ""}`

  return (
    <Section
      className={`relative flex ${props.bgColour ?? ""}  ${props.theme ? getThemeClass(props.theme, props.customBorder ?? undefined) : (props.customBorder ?? "")} `}
      flexHeight={props.flexHeight}
    >
      <div className={`relative mx-auto flex w-full ${props.maxWidth ?? "max-w-7xl"}`}>
        <div className={`container z-10 mx-auto flex h-full flex-col justify-center ${padding}`}>
          {typeof props.sectionTitle === "string" ? (
            <h3 className={`text-desk-resp-3xl mb-4 xl:mb-6 ${titleClass}`}>{props.sectionTitle}</h3>
          ) : (
            props.sectionTitle && <h3 className={`text-desk-resp-3xl mb-4 xl:mb-6 ${titleClass}`}>{props.sectionTitle}</h3>
          )}
          <div className={`flex flex-col justify-center sm:justify-between ${props.itemsClass ?? "md:flex-row"} ${props.alignItemsClass ?? "md:items-center"} ${props.gap ?? "gap-6"}`}>
            <div
              className={`${props.customTextWidth ?? "md:w-1/2"} ${props.textPadding ?? ""} ${props.textRight ? "order-last ml-auto" : ""} ${props.centredOnMobile ? "text-center md:text-left" : ""} `}
            >
              <div className={`TEXTs ${props.iconImage && !props.customDiv && !props.iconImgLeft ? "xl:mb-12" : ""} `}>
                {typeof props.title === "string" ? (
                  <h2 className={`text-desk-resp-4_5xl mb-4 ${titleClass}`}>
                    {props.title}
                    {props.inlineTitle && <p>{props.inlineTitle}</p>}
                  </h2>
                ) : (
                  props.title && <div className={`text-desk-resp-4_5xl ${titleClass}`}>{props.title}</div>
                )}

                {typeof props.pretitle === "string" ? (
                  <h3 className={`text-desk-resp-3xl mb-2 ${titleClass}`}>{props.pretitle}</h3>
                ) : (
                  props.pretitle && <div className={`text-desk-resp-3xl mb-2 ${titleClass}`}>{props.pretitle}</div>
                )}

                {props.body && (
                  <div className={`flex flex-col gap-5 mt-8 ${props.bodyWidth ?? ""} text-resp-xl ${props.theme === "light" ? "text-grey-85" : props.bodyColour ? props.bodyColour : "text-white"} `}>
                    {body}
                  </div>
                )}
              </div>
              {props.afterAction && <>{props.afterAction}</>}
            </div>

            {props.customDiv && <>{props.customDiv}</>}

            {props.image && <div className={`${props.imageClass ?? `sm:w-3/4 md:mx-0 md:w-1/2 ${props.extraImageClass}`} ${props.imgTop ? "order-first md:order-last" : ""} `}>{images}</div>}
          </div>
        </div>
        {props.iconImage && (
          <Image
            src={props.iconImage}
            alt="heart-image"
            className={`absolute bottom-0 w-72 md:w-96 lg:w-[28rem] xl:w-[32rem] ${props.iconImageClass ?? ""} ${props.iconImgLeft ? (props.iconImgPosition ?? `-left-12 right-0 lg:-left-16 xl:-left-24`) : (props.iconImgPosition ?? `-right-14 md:-right-16 xl:-right-24`)} ${props.hideIconImageOnMobile ? "hidden xs:block" : ""}`}
          />
        )}
      </div>
    </Section>
  )
}

export default SplitTextSection
